import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { localize } from '../../i18n/script';
import { courseApi, type CoursePurchaseStatus } from '../../services/courseApi';
import { trackPurchase } from '../../utils/analytics';

// Course-specific success page (does not reuse the booking success flow).
// WeChat payments are async, so the purchase may still be `pending` here; we
// poll a few times before telling the buyer it is still processing.
export default function CoursePurchaseSuccess() {
  const { i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, getToken } = useAuth();

  const [status, setStatus] = useState<CoursePurchaseStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const trackedRef = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (!sessionId) {
      setLoading(false);
      return;
    }
    // Stripe may redirect back after the local session expired, or the buyer
    // may open the success URL in another browser. Send them to login and
    // come back here so the purchase can still be confirmed.
    if (!isAuthenticated) {
      navigate(
        `/login?redirect=${encodeURIComponent(
          `/courses/${slug}/purchase-success?session_id=${sessionId}`
        )}`
      );
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async (): Promise<void> => {
      try {
        const token = await getToken();
        if (cancelled) return;
        // Stale auth: isAuthenticated is true but no usable token. Re-auth
        // instead of hanging on the loading screen forever.
        if (!token) {
          navigate(
            `/login?redirect=${encodeURIComponent(
              `/courses/${slug}/purchase-success?session_id=${sessionId}`
            )}`
          );
          return;
        }
        const data = await courseApi.getPurchaseStatus(sessionId, token);
        if (cancelled) return;
        setStatus(data);
        setLoading(false);
        if (data.owned && !trackedRef.current) {
          trackedRef.current = true;
          trackPurchase({
            type: 'course',
            transactionId: sessionId,
            // trackPurchase reports in USD; only send a value for USD purchases
            // so CNY (WeChat) amounts are not mis-recorded as USD revenue.
            value: data.currency === 'usd' && data.amountTotal ? data.amountTotal / 100 : undefined,
            title: data.course.title,
          });
        }
        // Keep polling while an async (WeChat) payment is still pending.
        if (!data.owned && data.status === 'pending' && attempts < 5) {
          attempts += 1;
          setTimeout(poll, 3000);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load purchase status');
        setLoading(false);
      }
    };
    void poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, isAuthenticated, authLoading, getToken, navigate, slug]);

  if (authLoading || loading) {
    return <div className="mx-auto max-w-2xl px-4 py-16 text-center text-gray-500">Loading...</div>;
  }

  const owned = status?.owned;
  const pending = status && !status.owned && status.status === 'pending';

  return (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center">
      {owned ? (
        <>
          <h1 className="mb-3 text-2xl font-semibold text-gray-900">Thank you for your purchase</h1>
          <p className="mb-6 text-gray-600">
            You now have permanent access to{' '}
            {status
              ? localize(status.course.titleCN, status.course.titleEN, i18n.language) ||
                status.course.title
              : ''}
            . You can watch it anytime after logging in.
          </p>
          <Link
            to={`/courses/${status?.course.slug || slug}/watch`}
            className="inline-block rounded-lg bg-orange-500 px-6 py-3 font-medium text-white hover:bg-orange-600"
          >
            Watch the course
          </Link>
        </>
      ) : pending ? (
        <>
          <h1 className="mb-3 text-2xl font-semibold text-gray-900">Payment processing</h1>
          <p className="mb-6 text-gray-600">
            Your payment is being confirmed. This can take a moment for WeChat Pay. You can refresh
            this page, or check Courses in My Home shortly.
          </p>
          <Link to="/dashboard?tab=courses" className="text-orange-600 hover:underline">
            Go to Courses
          </Link>
        </>
      ) : (
        <>
          <h1 className="mb-3 text-2xl font-semibold text-gray-900">Purchase not completed</h1>
          <p className="mb-6 text-gray-600">
            {error || 'We could not confirm this purchase. If you were charged, please contact us.'}
          </p>
          <Link to={`/courses/${slug}`} className="text-orange-600 hover:underline">
            Back to the course
          </Link>
        </>
      )}
    </div>
  );
}
